import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import ExpandMore from "@mui/icons-material/ExpandMore";

interface IProps {
  title: string;
  expanded: boolean;
  disabled: boolean;
  valid: boolean;
  children?: React.ReactNode;
  accordionKey: string;
  handleExpansionEvent: (accordionKey: string, expanded: boolean) => void;
  sx?: SxProps;
}

export const StepAccordionCard = (props: IProps) => {
  const {
    title,
    expanded = false,
    children,
    accordionKey,
    handleExpansionEvent,
    sx,
  } = props;

  const {
    section,
    sectionIcon,
    icon,
    accordion,
    disabled,
    valid,
    accordionSummary,
    accordionSummaryHeader,
    accordionDetails,
  } = sxStyles;

  const sectionIconStyles: SxProps = {
    ...sectionIcon,
    ...(props.disabled && disabled),
    ...(props.valid && valid),
  } as SxProps;

  return (
    <Card sx={{ ...section, ...sx } as SxProps}>
      <Accordion
        sx={accordion}
        expanded={expanded}
        onChange={(_, val) => {
          handleExpansionEvent(accordionKey, val);
        }}
        disableGutters
        disabled={props.disabled}
      >
        <AccordionSummary sx={accordionSummary}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexGrow={1}
          >
            <Box sx={sectionIconStyles}>
              <Check color="primary" sx={icon} />
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexGrow={1}
              sx={{
                ...accordionSummaryHeader,
              }}
            >
              <Typography variant="h5">{title}</Typography>
              <ExpandMore
                sx={{ transition: "all 0.2s", rotate: expanded ? "180deg" : 0 }}
              />
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetails}>
          <Box>{children}</Box>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

const sxStyles: { [key: string]: SxProps } = {
  section: {
    display: "flex",
    position: "relative",
    backgroundColor: "white.main",
    mb: 2,
  },
  sectionIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    mr: 3,
    borderRadius: "50%",
    transition: "backgroundColor 300ms",
    flexShrink: 0,
    border: `2px solid`,
    borderColor: "primary.main",
    color: "primary.main",
  },
  icon: {
    color: "currentcolor",
    transition: "color 300ms",
  },
  disabled: {
    borderColor: "grey.400",
    color: "grey.400",
  },
  valid: {
    borderColor: "primary.main",
    backgroundColor: "primary.main",
    color: "white.main",
  },
  accordion: {
    flexGrow: 1,
    boxShadow: 0,
    backgroundColor: "transparent !important",
    p: 0,
    mt: 0,
    overflow: "hidden",
    "&:before": {
      opacity: 0,
    },
  },
  accordionSummary: {
    m: 0,
    minHeight: 0,
    px: 3,
    "&.Mui-expanded": {
      minHeight: 0,
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      color: "black.main",
    },
    "& .MuiAccordionSummary-content": {
      m: 0,
    },
    "& p": {
      fontWeight: "700",
    },
  },
  accordionSummaryHeader: {
    boxShadow: "0 1px 0 0 #ff3c00",
    flexGrow: 1,
    py: 3,
  },
  accordionDetails: {
    pt: 2,
    pb: 5,
    pl: 10,
    pr: 3,
  },
};
